import React from 'react';
import { observer } from 'mobx-react';
import { action, makeObservable, observable } from 'mobx';
import { Drawer, Empty, Space, Spin, Table, Tag, Typography, notification } from 'antd';
import { DesignerAvatar } from '../common/designerAvatar';
import { ChallengesProxy } from '../services/catalogServiceAPI/challengesProxy';
import { RENDERINGS_BASE } from './constants';

const POLL_INTERVAL_MS = 10000;

@observer
export class NonSubmittersDrawer extends React.Component {
    challengesProxy = new ChallengesProxy();

    @observable rows = [];
    @observable isLoading = false;

    constructor(props) {
        super(props);
        makeObservable(this);
    }

    @action.bound setIsLoading = (v) => this.isLoading = v;
    @action.bound setRows = (v) => this.rows.replace(v);

    componentDidMount() {
        if (this.props.challenge) {
            this.fetch();
            this.startPollingIfLive();
        }
    }

    componentDidUpdate(prevProps) {
        const prevId = prevProps.challenge?.id;
        const currId = this.props.challenge?.id;
        if (prevId !== currId) {
            this.stopPolling();
            this.setRows([]);
            if (this.props.challenge) {
                this.fetch();
                this.startPollingIfLive();
            }
        } else if (this.props.challenge && prevProps.challenge?.status !== this.props.challenge.status) {
            this.stopPolling();
            this.startPollingIfLive();
        }
    }

    componentWillUnmount() {
        this.stopPolling();
    }

    startPollingIfLive() {
        if (this.props.challenge?.status !== 'active') return;
        this.pollTimer = setInterval(() => {
            if (document.visibilityState === 'visible') this.fetch({ silent: true });
        }, POLL_INTERVAL_MS);
    }

    stopPolling() {
        if (this.pollTimer) {
            clearInterval(this.pollTimer);
            this.pollTimer = null;
        }
    }

    async fetch({ silent = false } = {}) {
        const id = this.props.challenge?.id;
        if (!id) return;
        if (!silent) this.setIsLoading(true);
        try {
            const rows = await this.challengesProxy.listNonSubmitters(id);
            this.setRows(rows);
        } catch (e) {
            if (!silent) notification.error({ message: 'Failed to load non-submitters', description: e.message });
        } finally {
            if (!silent) this.setIsLoading(false);
        }
    }

    columns = [
        {
            title: 'User',
            dataIndex: 'userName',
            render: (userName, row) => (
                <Space>
                    <DesignerAvatar avatar={row.avatar} photoURL={row.userAvatar} />
                    <Space direction="vertical" size={0}>
                        <span>{userName}</span>
                        <Typography.Text type="secondary" copyable style={{ fontSize: 12 }}>{row.userId}</Typography.Text>
                    </Space>
                </Space>
            ),
        },
        {
            title: 'Room',
            dataIndex: 'roomId',
            render: (roomId) => roomId ? (
                <a
                    href={`https://designwithfriends.com/editor/${roomId}`}
                    target="_blank"
                    rel="noopener noreferrer"
                >
                    {roomId.slice(0, 8)}…
                </a>
            ) : '—',
        },
        {
            title: 'Rendered?',
            dataIndex: 'hasRendered',
            width: 110,
            render: (hasRendered, row) => hasRendered ? (
                row.renderingId ? (
                    <a href={`${RENDERINGS_BASE}/${row.renderingId}`} target="_blank" rel="noopener noreferrer">
                        <Tag color="green">Yes</Tag>
                    </a>
                ) : <Tag color="green">Yes</Tag>
            ) : <Tag>No</Tag>,
        },
        {
            title: 'Entered',
            dataIndex: 'enteredAt',
            width: 180,
            render: (v) => v ? new Date(v).toLocaleString() : '—',
        },
    ];

    render() {
        const { challenge, open, onClose } = this.props;
        const isLive = challenge?.status === 'active';
        return (
            <Drawer
                title={
                    <Space>
                        <span>Not submitted{challenge ? ` — ${challenge.title}` : ''}</span>
                        {isLive && <Tag color="green">LIVE</Tag>}
                    </Space>
                }
                width={720}
                placement="right"
                open={open}
                onClose={onClose}
                destroyOnClose
            >
                {this.isLoading ? (
                    <Spin />
                ) : this.rows.length === 0 ? (
                    <Empty description="Everyone who entered has submitted" />
                ) : (
                    <>
                        <Typography.Text type="secondary">
                            {this.rows.length} user{this.rows.length === 1 ? '' : 's'} entered but didn't submit
                        </Typography.Text>
                        <Table
                            rowKey="userId"
                            dataSource={this.rows.slice()}
                            columns={this.columns}
                            pagination={{ pageSize: 20 }}
                            style={{ marginTop: 12 }}
                        />
                    </>
                )}
            </Drawer>
        );
    }
}
